<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const isExpanded = ref(false);

const toggle = () => {
  isExpanded.value = !isExpanded.value;
};

watch(
  () => props.data.content,
  () => {
    isExpanded.value = false;
  },
);
</script>
<template>
  <section
    v-if="data?.content"
    :id="data.id"
    class="section"
    :class="[
      data?.background_color && data.template !== 'article'
        ? `bg-${data.background_color}`
        : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div :class="['container', `container--${data?.template}`]">
      <div
        :class="[
          'seo-text label',
          data.background_color === 'black' && data.template !== 'article'
            ? 'white'
            : 'black',
        ]"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
      >
        <div
          class="seo-text__content"
          :class="{ 'seo-text__content--collapsed': !isExpanded }"
          :style="{ '--max-lines': 3 }"
          v-html="data.content"
        />
        <div v-if="data.content" class="seo-text__show">
          <Button
            text
            size="small"
            class="seo-text__show-button link-normal"
            @click="toggle"
          >
            <span>
              {{
                isExpanded ? $t('filters.text_hide') : $t('filters.text_show')
              }}
            </span>
            <CIcon
              name="direction-small-black"
              width="16"
              height="16"
              :class="[
                isExpanded
                  ? 'seo-text__show-button-arrow seo-text__show-button-arrow--up'
                  : 'seo-text__show-button-arrow',
              ]"
            />
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.seo-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px;
  text-align: left;
  @include media-breakpoint-up(md) {
    padding: 0 48px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(padding-left, 48);
    @include responsive-spacing(padding-right, 48);
  }
  &:deep(p:last-child) {
    margin-bottom: 0;
  }
  &:deep(img),
  &:deep(video) {
    border-radius: 8px;
    height: auto;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
  }
  &:deep(ul) {
    list-style: none;
    padding-left: 24px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-left, 24);
    }
  }
  &:deep(ul li) {
    margin-top: 16px;
    position: relative;
    &:before {
      background: url('/images/style/check.svg') no-repeat 0 0;
      background-size: contain;
      content: '';
      height: 16px;
      left: -24px;
      top: 5px;
      position: absolute;
      width: 16px;
      z-index: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-top, 16);
      &:before {
        @include responsive-spacing(left, -24);
        @include responsive-spacing(top, 5);
        @include responsive-spacing(height, 16);
        @include responsive-spacing(width, 16);
      }
    }
  }
  &:deep(ol) {
    @include body-1-bold;
    padding-left: 0;
  }
  &:deep(ol li) {
    margin-top: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-top, 16);
    }
  }
  &:deep(h2) {
    @include h4;
  }
  &:deep(h3) {
    @include h5;
  }
  &:deep(h4) {
    @include h6;
  }
  &:deep(h5) {
    @include body-1-bold;
  }
  &:deep(h6) {
    @include body-2-bold;
  }
  &__show {
    margin-left: auto;
    &-button {
      align-items: center;
      display: grid;
      justify-content: start;
      gap: 8px;
      grid-template-columns: auto 16px;
      padding: 0 12px !important;
      @include media-breakpoint-up(md) {
        padding: 0 4px !important;
      }
      @include media-breakpoint-up(xl) {
        grid-template-columns: auto px-to-vw(16);
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-left, 4, true);
        @include responsive-spacing(padding-right, 4, true);
      }
      &-arrow {
        height: auto;
        transform: rotate(90deg);
        width: 100%;
        &--up {
          transform: rotate(-90deg);
        }
      }
    }
  }
  &__content {
    overflow: hidden;
    transition: max-height 0.3s ease;
    line-height: 1.5;

    &--collapsed {
      display: -webkit-box;
      -webkit-line-clamp: var(--max-lines);
      -webkit-box-orient: vertical;
    }
    &:deep(p) {
      margin-bottom: 8px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
